import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Scale, FileText, AlertTriangle, Clock, Mail, CheckCircle, XCircle } from 'lucide-react';

export default function LawEnforcement() {
  const lastUpdated = "April 4, 2026";

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0A0A1B 0%, #0D0D1F 100%)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(10, 10, 27, 0.8)', borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">Back to Privluxe</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.6)' }}>Legal</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.2) 0%, rgba(239,68,68,0.2) 100%)', border: '1px solid rgba(245,158,11,0.3)' }}>
            <Scale className="h-8 w-8" style={{ color: '#F59E0B' }} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Law Enforcement Guidelines</h1>
          <p style={{ color: 'rgba(229,231,235,0.6)' }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Notice */}
        <div className="p-6 rounded-2xl mb-12" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <p className="text-center" style={{ color: '#E5E7EB' }}>
            <strong className="text-white">For Law Enforcement Officials Only</strong><br />
            This page outlines the process for law enforcement agencies to request user information from Privluxe.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {/* Overview */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <FileText className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Overview</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Privluxe is committed to maintaining user privacy while cooperating with valid legal processes. We comply with applicable laws and regulations while ensuring proper legal procedures are followed.</p>
              <p>This guide provides information about the types of data we maintain, how law enforcement can submit requests, and what information may be disclosed in response to valid legal process.</p>
            </div>
          </section>

          {/* Types of Data */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <FileText className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Types of Data Available</h2>
            </div>
            <div className="space-y-6" style={{ color: 'rgba(229,231,235,0.8)' }}>
              {/* Data We May Have */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" style={{ color: '#22C55E' }} />
                  Data We May Have
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Account Information:</strong> Phone number, email, username, profile picture, account creation date</li>
                  <li>• <strong>Login Records:</strong> IP addresses, timestamps, device information</li>
                  <li>• <strong>Connection Data:</strong> Contact list associations, conversation participants</li>
                  <li>• <strong>Transaction Logs:</strong> Message delivery timestamps, call logs (duration, participants)</li>
                </ul>
              </div>

              {/* Data We Do Not Have */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <XCircle className="h-5 w-5" style={{ color: '#EF4444' }} />
                  Data We Do Not Have / Cannot Provide
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Message Content:</strong> Messages that have been deleted by users</li>
                  <li>• <strong>View-Once Media:</strong> Automatically deleted after viewing</li>
                  <li>• <strong>Call Content:</strong> We do not record voice or video calls</li>
                  <li>• <strong>Encryption Keys:</strong> End-to-end encrypted content cannot be decrypted</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Legal Process */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <Scale className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Required Legal Process</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>We require valid legal process before disclosing user information:</p>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold text-white mb-2">Basic Subscriber Information</h4>
                  <p className="text-sm">Requires: Valid subpoena, court order, or equivalent legal process</p>
                </div>
                
                <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold text-white mb-2">Transactional Records</h4>
                  <p className="text-sm">Requires: Court order under applicable law (e.g., Section 91 CrPC in India)</p>
                </div>
                
                <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold text-white mb-2">Content of Communications</h4>
                  <p className="text-sm">Requires: Valid search warrant or court order under applicable interception laws</p>
                </div>
              </div>
            </div>
          </section>

          {/* Emergency Requests */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <AlertTriangle className="h-5 w-5" style={{ color: '#EF4444' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Emergency Requests</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>In cases involving imminent danger to life or serious physical injury, we may voluntarily disclose information to law enforcement without prior legal process.</p>
              <p>Emergency requests must:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Come from a verified law enforcement official</li>
                <li>Describe the nature of the emergency with specificity</li>
                <li>Identify the specific information needed</li>
                <li>Explain how the information will help address the emergency</li>
              </ul>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <p className="text-sm"><strong className="text-white">Emergency Contact:</strong> emergency@privluxe.com (monitored 24/7 for genuine emergencies)</p>
              </div>
            </div>
          </section>

          {/* Submission Process */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <Mail className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">How to Submit Requests</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>All requests must include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Official letterhead from the requesting agency</li>
                <li>Name, badge number, and contact information of requesting officer</li>
                <li>Case or investigation reference number</li>
                <li>Specific account identifiers (phone number, username, or user ID)</li>
                <li>Specific data requested</li>
                <li>Legal basis for the request</li>
                <li>Return address for response</li>
              </ul>
              
              <div className="p-6 rounded-xl mt-6" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
                <h3 className="text-lg font-semibold text-white mb-3">Submit Requests To:</h3>
                <p><strong className="text-white">Email:</strong> lawenforcement@privluxe.com</p>
                <p className="mt-2"><strong className="text-white">Mail:</strong></p>
                <p>Privluxe Legal Department<br />
                Law Enforcement Requests<br />
                [Address to be provided upon registration]</p>
              </div>
            </div>
          </section>

          {/* Response Time */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <Clock className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Response Timeline</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Emergency Requests:</strong> Within 24 hours</li>
                <li><strong className="text-white">Standard Requests:</strong> Within 14 business days</li>
                <li><strong className="text-white">Complex Requests:</strong> May require additional time (we will notify you)</li>
              </ul>
            </div>
          </section>

          {/* User Notification */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <AlertTriangle className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">User Notification</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Our policy is to notify users of requests for their information before disclosure, unless:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We are legally prohibited from doing so (e.g., court order, gag order)</li>
                <li>Notification would jeopardize an investigation</li>
                <li>There is a risk of harm to any individual</li>
                <li>The matter involves child safety</li>
              </ul>
            </div>
          </section>

          {/* Preservation Requests */}
          <section>
            <div className="p-6 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 className="text-lg font-semibold text-white mb-3">Data Preservation Requests</h3>
              <p style={{ color: 'rgba(229,231,235,0.7)' }}>We will preserve account records for 90 days upon receipt of a valid preservation request while law enforcement obtains appropriate legal process. Preservation requests must identify specific accounts and the specific records to be preserved.</p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/privacy" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#A78BFA' }} />
              <span className="text-sm text-white">Privacy Policy</span>
            </Link>
            <Link to="/terms" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#22D3EE' }} />
              <span className="text-sm text-white">Terms & Conditions</span>
            </Link>
            <Link to="/acceptable-use" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <Scale className="h-6 w-6 mx-auto mb-2" style={{ color: '#EF4444' }} />
              <span className="text-sm text-white">Acceptable Use</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p style={{ color: 'rgba(229,231,235,0.4)' }}>© 2026 Privluxe. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
